import { useMemo, useState } from 'react';

import { Button, ExcelIcon, Form, Select, Text, TypeSelectOption, toastService } from '@thefarmersfront/kitchen';

import { PageTitle } from '~/components/common';
import { PageWrapper } from '~/components/layout';

import { useToggle } from '~/hooks/common';
import { usePostLotLedgersExcelDownload } from '~/hooks/stock';

import { BREAD_CRUMB, PATH, TOAST_MESSAGE } from '~/constants';
import { STOCK_LEDGER_MONTH_OPTIONS, STOCK_LEDGER_START, getStockLedgerYearOptions } from '~/constants/stock';

import type { TypeErrorResponseData } from '~/types/common.types';
import type { TypeStockLedgerFilter } from '~/types/stock.types';

import styles from './StockLedgerPage.module.scss';

const now = new Date();
const CURRENT_YEAR = now.getFullYear();
const CURRENT_MONTH = now.getMonth() + 1;

const toMonthOption = (month: number): TypeSelectOption<number> => ({
  value: month,
  text: `${month}월`,
});

const resolveNextMonth = (selectedYear: number, currentMonth: TypeSelectOption<number>): TypeSelectOption<number> => {
  if (selectedYear === CURRENT_YEAR && currentMonth.value > CURRENT_MONTH) {
    return toMonthOption(CURRENT_MONTH);
  }

  if (selectedYear === STOCK_LEDGER_START.year && currentMonth.value < STOCK_LEDGER_START.month) {
    return toMonthOption(STOCK_LEDGER_START.month);
  }

  return currentMonth;
};

const INITIAL_STOCK_LEDGER_FILTER: TypeStockLedgerFilter = {
  year: { value: CURRENT_YEAR, text: `${CURRENT_YEAR}년` },
  month: toMonthOption(CURRENT_MONTH),
};

const StockLedgerPage = () => {
  const [filter, setFilter] = useState<TypeStockLedgerFilter>(INITIAL_STOCK_LEDGER_FILTER);

  const { isToggle: isFilterOpen, handleToggle: handleFilterToggle } = useToggle({ initialState: true });

  const { postLotLedgersExcelDownload } = usePostLotLedgersExcelDownload();

  const yearOptions = useMemo(() => getStockLedgerYearOptions(), []);

  const monthOptions = useMemo(() => {
    return STOCK_LEDGER_MONTH_OPTIONS.filter((option) => {
      if (filter.year.value === STOCK_LEDGER_START.year && option.value < STOCK_LEDGER_START.month) {
        return false;
      }

      if (filter.year.value === CURRENT_YEAR && option.value > CURRENT_MONTH) {
        return false;
      }

      return true;
    });
  }, [filter.year.value]);

  const handleChangeYear = (_: unknown, selectedOption: TypeSelectOption<number>) => {
    setFilter((prev) => ({
      ...prev,
      year: selectedOption,
      month: resolveNextMonth(selectedOption.value, prev.month),
    }));
  };

  const handleChangeMonth = (_: unknown, selectedOption: TypeSelectOption<number>) => {
    setFilter((prev) => ({ ...prev, month: selectedOption }));
  };

  const handleSubmitSearch = (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
  };

  const fetchLotLedgerExcelDownload = async () => {
    const downloadingToastId = toastService.addToast({ message: TOAST_MESSAGE.DOWNLOADING, persist: true });

    try {
      await postLotLedgersExcelDownload({
        year: filter.year.value,
        month: filter.month.value,
      });

      toastService.addToast({
        message: TOAST_MESSAGE.DOWNLOAD_SUCCESS,
      });
    } catch (error) {
      toastService.addToast({
        type: 'error',
        message: (error as TypeErrorResponseData['error'])?.message || TOAST_MESSAGE.DEFAULT_ERROR,
      });
    } finally {
      toastService.removeToast(downloadingToastId);
    }
  };

  const handleClickExcelDownload = () => {
    fetchLotLedgerExcelDownload();
  };

  return (
    <PageWrapper>
      <PageTitle
        title="재고수불부"
        breadcrumbs={BREAD_CRUMB[PATH.STOCK_LEDGER]}
        handleFilterToggle={handleFilterToggle}
      />
      {isFilterOpen && (
        <Form className={styles['filter-section']} onSubmit={handleSubmitSearch}>
          <div className={styles['filters-container']}>
            <div className={styles['filter-container']}>
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                년 선택
              </Text>
              <Select value={filter.year} options={yearOptions} size="small" onChange={handleChangeYear} />
            </div>
            <div className={styles['filter-container']}>
              <Text className={styles['filter-label']} weight="semi-bold" size="small">
                월 선택
              </Text>
              <Select value={filter.month} options={monthOptions} size="small" onChange={handleChangeMonth} />
            </div>
          </div>
          <div className={styles['download-section']}>
            <Button
              variant="outlined"
              color="tertiary"
              size="tiny"
              leftIcon={<ExcelIcon size={16} />}
              onClick={handleClickExcelDownload}
            >
              엑셀 다운로드
            </Button>
          </div>
        </Form>
      )}
    </PageWrapper>
  );
};

export default StockLedgerPage;
